/**
 * NewConversationDialog Component
 * Dialog for creating new AI conversations
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { CreateConversationRequest } from '../../services/AIConsoleService';
import { useProjectService } from '../../services/ProjectService';

interface NewConversationDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (request: CreateConversationRequest) => Promise<void>;
  defaultProjectId?: number;
  defaultContextType?: 'general' | 'project' | 'technical' | 'estimation';
}

interface Project {
  id: number;
  name: string;
  description: string;
}

const NewConversationDialog: React.FC<NewConversationDialogProps> = ({
  open,
  onClose,
  onCreate,
  defaultProjectId,
  defaultContextType = 'general'
}) => {
  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState<number | undefined>(defaultProjectId);
  const [contextType, setContextType] = useState<'general' | 'project' | 'technical' | 'estimation'>(defaultContextType);
  const [customSystemPrompt, setCustomSystemPrompt] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const projectService = useProjectService();

  useEffect(() => {
    if (open) {
      loadProjects();
      // Reset form when dialog opens
      setTitle('');
      setProjectId(defaultProjectId);
      setContextType(defaultContextType);
      setCustomSystemPrompt('');
      setError(null);
    }
  }, [open, defaultProjectId, defaultContextType]);

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const projectsData = await projectService.getProjects();
      setProjects(projectsData || []);
    } catch (err) {
      console.error('Failed to load projects:', err);
      // Don't show error for projects, just leave the list empty
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleCreate = async () => {
    if (!title.trim()) {
      setError('Please enter a conversation title');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onCreate({
        title: title.trim(),
        project_id: projectId,
        context_type: contextType,
        custom_system_prompt: customSystemPrompt.trim() || undefined
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create conversation');
      setLoading(false);
    }
  };

  const getContextTypeDescription = (type: string): string => {
    switch (type) {
      case 'general':
        return 'General assistance with any questions or tasks';
      case 'project':
        return 'Project-specific questions, planning, and technical decisions';
      case 'technical':
        return 'Technical assistance with code, architecture, and implementation';
      case 'estimation':
        return 'Project estimation, timeline planning, and resource allocation';
      default:
        return '';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="new-conversation-dialog"
    >
      <DialogTitle>New AI Conversation</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            fullWidth
            label="Conversation Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Project Planning Discussion"
            required
            autoFocus
            data-testid="conversation-title-input"
          />

          <FormControl fullWidth>
            <InputLabel id="context-type-label">Context Type</InputLabel>
            <Select
              labelId="context-type-label"
              value={contextType}
              onChange={(e) => setContextType(e.target.value as any)}
              label="Context Type"
              data-testid="context-type-select"
            >
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="project">Project</MenuItem>
              <MenuItem value="technical">Technical</MenuItem>
              <MenuItem value="estimation">Estimation</MenuItem>
            </Select>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
              {getContextTypeDescription(contextType)}
            </Typography>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="project-label">Project (Optional)</InputLabel>
            <Select
              labelId="project-label"
              value={projectId || ''}
              onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : undefined)}
              label="Project (Optional)"
              disabled={loadingProjects}
              data-testid="project-select"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
            {loadingProjects && (
              <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="textSecondary">
                  Loading projects...
                </Typography>
              </Box>
            )}
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Custom System Prompt (Optional)"
            value={customSystemPrompt}
            onChange={(e) => setCustomSystemPrompt(e.target.value)}
            placeholder="Customize how the AI should behave in this conversation..."
            data-testid="custom-prompt-input"
            helperText="Override the default system prompt with custom instructions"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading} data-testid="cancel-button">
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={loading || !title.trim()}
          data-testid="create-button"
        >
          {loading ? <CircularProgress size={24} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewConversationDialog;
